import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import ar from './locales/ar.json';

const resources = {
  en: { translation: en },
  ar: { translation: ar },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'app_language',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: false,
    },

    // Ensure translation keys are properly resolved
    returnEmptyString: false,
    returnNull: false,
    saveMissing: false,
  });

export default i18n;

/**
 * Call this once per portal (before the React tree renders) to deep-merge
 * portal-specific translations into the shared i18n instance.
 *
 * Portal locale files should export a single top-level namespace key, e.g.:
 *   { admin: { navigation: { ... }, tracks: { ... } } }
 *   { sales: { navigation: { ... }, leads: { ... } } }
 *
 * Usage in App.tsx (or main.tsx):
 *   import { addPortalTranslations } from '@repo/settings';
 *   import en from './locales/en.json';
 *   import ar from './locales/ar.json';
 *   addPortalTranslations({ en, ar });
 */
export const addPortalTranslations = (portalResources: { en: object; ar: object }) => {
  (['en', 'ar'] as const).forEach((lang) => {
    const existing = i18n.getResourceBundle(lang, 'translation') || {};
    const merged = deepMerge(existing, portalResources[lang]);
    i18n.addResourceBundle(lang, 'translation', merged, true, true);
  });
};

/** Recursive deep merge — portal values override shared values on conflict */
function deepMerge(target: Record<string, any>, source: Record<string, any>): Record<string, any> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object'
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

// Helper to check if current language is RTL
export const isRTL = (lang?: string) => {
  const currentLang = lang || i18n.language;
  return currentLang === 'ar';
};

// Helper to get translation with fallback
export const getTranslation = (key: string, fallback?: string): string => {
  const translation = i18n.t(key);
  // If translation is the same as the key, it means it's missing
  if (translation === key) {
    return fallback || key;
  }
  return translation;
};

// Available languages
export const languages = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
] as const;

export type LanguageCode = (typeof languages)[number]['code'];
